import AppContext from "PFApp/app_context";
import { LandingLayoutWithoutSidebar } from "PFApp/components/landing_layout";
import { Avatar } from "PFComponents/avatar";
import { LoadingDots } from "PFComponents/loading_dots";
import { GROWL_ACTIONS } from "PFCore/reducers/growl_reducer";
import { ApiRoute } from "PFCore/utilities/routes";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import OnboardingBox from "../parts/onboarding_box";
import OnboardingHeader from "../parts/onboarding_header";
import ReviewCustomField from "../parts/review_custom_field";
import ReviewPosition from "../parts/review_position";
import ReviewTogleableSection from "../parts/review_toggleable_section";
import { useOnboarding } from "../use_onboarding";
import css from "./common.less";

const ReviewStep = () => {
  const { customTypes, stepDotsProps, changeRequests, currentProfile, navigateNext, navigateBack } =
    useOnboarding("review");
  const { dispatch } = useContext(AppContext);
  const { t } = useTranslation("onboarding");
  const [changeRequestIndex, setChangeRequestIndex] = useState(0);
  const changeRequest = changeRequests && changeRequests[changeRequestIndex];

  const [changedData, setChangedData] = useState({ customFields: {}, positions: [] });
  const [disabledSections, setDisabledSections] = useState({});

  const [errors, setErrors] = useState({});

  if (changeRequests && changeRequests.length === 0) {
    navigateNext();
    return false;
  }

  const next = () => {
    const applied_data = {
      id: currentProfile.id
    };

    if (changeRequest.data.avatar && !disabledSections["avatar"]) {
      applied_data.avatar = changeRequest.data.avatar;
    }

    if (changeRequest.data.custom_fields) {
      applied_data.custom_fields = changeRequest.data.custom_fields
        .filter(({ type }) => !disabledSections[`cf_${type.name}`])
        .map((customField) => {
          const { name } = customField.type;
          return {
            type: customField.type,
            values: changedData.customFields[name] || customField.values.map(({ value }) => ({ value }))
          };
        });

      if (applied_data.custom_fields.length === 0) {
        delete applied_data.custom_fields;
      }
    }

    if (changeRequest.data.positions) {
      applied_data.positions = changeRequest.data.positions
        .filter(({ ordinal }) => !disabledSections[`position_${ordinal}`])
        .map((position) => changedData.positions[position.ordinal] || position);

      if (applied_data.positions.length === 0) {
        delete applied_data.positions;
      }
    }

    $.ajax({
      method: "PUT",
      api_version: 2,
      headers: { "Content-Type": "application/json" },
      url: ApiRoute(`/api/change_requests/${changeRequest.id}/apply`),
      data: JSON.stringify({ applied_data })
    })
      .then(() => {
        if (changeRequestIndex + 1 >= changeRequests.length) {
          navigateNext();
        } else {
          setChangeRequestIndex(changeRequestIndex + 1);
        }
      })
      .catch((resp) => {
        setErrors(resp.responseJSON.errors);
        dispatch({
          type: GROWL_ACTIONS.GROWL_RENDER,
          payload: {
            kind: "error",
            message: t("stepNotSaved")
          }
        });
      });
  };

  const toggleSection = (section) => setDisabledSections((prev) => ({ ...prev, [section]: !prev[section] }));

  // api's format to actually usable: { [typeName]: "string error" }
  const profileCfErrors =
    errors.profile
      ?.map(({ errors }) => errors?.custom_fields?.reduce((mem, cf) => ({ ...mem, ...cf }), {}))
      .reduce((mem, errorItem) => ({ ...mem, ...errorItem }), {}) || {};

  return (
    <LandingLayoutWithoutSidebar alignTop>
      <OnboardingBox big qaId="Onboarding.ReviewStep" stepDotsProps={stepDotsProps}>
        {changeRequest ? (
          <>
            <OnboardingHeader
              title={t("reviewCurrentTotal", {
                current: changeRequests.indexOf(changeRequest) + 1,
                total: changeRequests.length
              })}
              back={navigateBack}
              next={next}
            />
            <p className={css.tip}>{t("reviewParsedInformation")}</p>

            {changeRequest?.data?.avatar?.url && (
              <>
                <ReviewTogleableSection
                  fadeMode
                  collapsedTitle={t("avatar")}
                  disabled={disabledSections["avatar"]}
                  handleToggle={() => toggleSection("avatar")}
                  style={{ marginBottom: 50 }}
                >
                  <div style={{ textAlign: "center" }}>
                    <Avatar size={144} imagePath={changeRequest?.data?.avatar?.url} />
                  </div>
                </ReviewTogleableSection>
              </>
            )}

            {changeRequest?.data?.custom_fields?.map((customField, index) => {
              const customType = customTypes.find(({ name }) => name === customField.type.name);
              return (
                <ReviewTogleableSection
                  collapsedTitle={customType.display_as}
                  style={{ marginBottom: 50, zIndex: 2000 - index /* fix for the dropdowns */ }}
                  key={customField.type.name}
                  disabled={disabledSections[`cf_${customField.type.name}`]}
                  handleToggle={() => toggleSection(`cf_${customField.type.name}`)}
                >
                  <ReviewCustomField
                    error={profileCfErrors[customField.type.name]}
                    key={customField.type.name}
                    customField={customField}
                    handleChange={(values) =>
                      setChangedData({
                        ...changedData,
                        customFields: { ...changedData.customFields, [customField.type.name]: values }
                      })
                    }
                  />
                </ReviewTogleableSection>
              );
            })}

            {changeRequest?.data?.positions?.map((position, index) => {
              position = changedData.positions[position.ordinal] || position;
              const title = position.title || "position";

              return (
                <ReviewTogleableSection
                  collapsedTitle={position.subtitle ? `${title} in ${position.subtitle}` : title}
                  style={{ marginBottom: 50, zIndex: 1000 - index /* fix for the dropdowns */ }}
                  key={position.ordinal}
                  disabled={disabledSections[`position_${position.ordinal}`]}
                  handleToggle={() => toggleSection(`position_${position.ordinal}`)}
                >
                  <ReviewPosition
                    key={position.ordinal}
                    errors={
                      errors.positions?.find(({ ordinal }) => ordinal === position.ordinal)?.errors || {}
                    }
                    position={position}
                    style={{ marginBottom: 30 }}
                    handleChange={(position) => {
                      // there can be only one
                      if (position.main) {
                        Object.values(changedData.positions).forEach((position) => (position.main = false));
                      }

                      setChangedData({
                        ...changedData,
                        positions: { ...changedData.positions, [position.ordinal]: position }
                      });
                    }}
                  />
                </ReviewTogleableSection>
              );
            })}
          </>
        ) : (
          <LoadingDots />
        )}
      </OnboardingBox>
    </LandingLayoutWithoutSidebar>
  );
};

export default ReviewStep;
